
import { useState } from "react";
import { Roadmap } from "@/types";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Settings, Star, Trash2 } from "lucide-react";
import RoadmapStatusManagement from "./roadmap/RoadmapStatusManagement";
import RoadmapBoard from "./RoadmapBoard";
import { useRoadmapData } from "@/hooks/useRoadmapData";
import { canCreateRoadmap } from "@/utils/planLimits";
import { UpgradePrompt } from "@/components/shared/UpgradePrompt";

type RoadmapManagementProps = {
  companyId: string;
  feedback: any[];
  companyStatuses?: any[];
  onFeedbackUpdate: () => Promise<void>;
  companyPlan: string;
};

const RoadmapManagement = ({ 
  companyId, 
  feedback, 
  companyStatuses = [],
  onFeedbackUpdate,
  companyPlan
}: RoadmapManagementProps) => {
  const [isCreatingRoadmap, setIsCreatingRoadmap] = useState(false);
  const [newRoadmapName, setNewRoadmapName] = useState("");
  const [newRoadmapDescription, setNewRoadmapDescription] = useState("");
  const [isEditingStatusesOpen, setIsEditingStatusesOpen] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  
  const { 
    roadmaps, 
    isLoading, 
    selectedRoadmapId, 
    setSelectedRoadmapId,
    selectedRoadmap,
    createRoadmap,
    deleteRoadmap,
    setDefaultRoadmap
  } = useRoadmapData(companyId);

  const handleOpenCreateRoadmap = () => {
    const check = canCreateRoadmap(companyPlan, roadmaps.length);
    if (!check.allowed) {
      setUpgradeMessage(check.message || "");
      setShowUpgradePrompt(true);
      return;
    }
    setIsCreatingRoadmap(true);
  };

  const handleCreateRoadmap = async () => {
    if (!newRoadmapName.trim()) return;
    
    await createRoadmap({
      name: newRoadmapName,
      description: newRoadmapDescription
    });
    
    setNewRoadmapName("");
    setNewRoadmapDescription("");
    setIsCreatingRoadmap(false);
  };

  const handleDeleteRoadmap = async () => {
    if (!selectedRoadmapId) return;
    
    const confirmed = window.confirm("Are you sure you want to delete this roadmap?");
    if (!confirmed) return;
    
    await deleteRoadmap(selectedRoadmapId);
  };

  const handleSetDefaultRoadmap = async () => {
    if (!selectedRoadmapId) return;
    await setDefaultRoadmap(selectedRoadmapId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <p className="text-muted-foreground">Loading roadmaps...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select
            value={selectedRoadmapId || ""}
            onValueChange={(value) => setSelectedRoadmapId(value)}
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select a roadmap" />
            </SelectTrigger>
            <SelectContent>
              {roadmaps.map((roadmap) => (
                <SelectItem key={roadmap.id} value={roadmap.id}>
                  <div className="flex items-center gap-2">
                    {roadmap.is_default && <Star className="h-4 w-4 text-yellow-500" />}
                    {roadmap.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" onClick={handleOpenCreateRoadmap}>
            <Plus className="h-4 w-4 mr-2" />
            New Roadmap
          </Button>

          <Dialog open={isCreatingRoadmap} onOpenChange={setIsCreatingRoadmap}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Roadmap</DialogTitle>
                <DialogDescription>
                  Add a new roadmap to organize your feedback items.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={newRoadmapName}
                    onChange={(e) => setNewRoadmapName(e.target.value)}
                    placeholder="Product Roadmap"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={newRoadmapDescription}
                    onChange={(e) => setNewRoadmapDescription(e.target.value)}
                    placeholder="Describe the purpose of this roadmap"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreatingRoadmap(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateRoadmap}>Create Roadmap</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {selectedRoadmap && (
          <div className="flex items-center gap-2">
            <Dialog open={isEditingStatusesOpen} onOpenChange={setIsEditingStatusesOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Statuses
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>Manage Roadmap Statuses</DialogTitle>
                  <DialogDescription>
                    Select which statuses should appear in the "{selectedRoadmap.name}" roadmap.
                  </DialogDescription>
                </DialogHeader>
                <RoadmapStatusManagement 
                  roadmap={selectedRoadmap} 
                  companyStatuses={companyStatuses}
                  onClose={() => setIsEditingStatusesOpen(false)}
                />
              </DialogContent>
            </Dialog>
            
            {!selectedRoadmap.is_default && (
              <Button variant="outline" size="sm" onClick={handleSetDefaultRoadmap}>
                <Star className="h-4 w-4 mr-2" />
                Set as Default
              </Button>
            )}
            
            {!selectedRoadmap.is_default && roadmaps.length > 1 && (
              <Button variant="destructive" size="sm" onClick={handleDeleteRoadmap}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedRoadmap ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {selectedRoadmap.name}
                  {selectedRoadmap.is_default && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      Default
                    </span>
                  )}
                </CardTitle>
                {selectedRoadmap.description && (
                  <CardDescription>{selectedRoadmap.description}</CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RoadmapBoard 
              feedback={feedback}
              companyStatuses={companyStatuses}
              onFeedbackUpdate={onFeedbackUpdate}
              roadmap={selectedRoadmap}
              isReadOnly={false}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            {roadmaps.length === 0
              ? "No roadmaps found. Create your first roadmap to get started."
              : "Select a roadmap to view and manage."
            }
          </p>
        </div>
      )}

      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        message={upgradeMessage}
      />
    </div>
  );
};

export default RoadmapManagement;
